import { Button } from "@/components/ui/button";
import { Plus, Sparkles, ArrowRight, Calculator, Gift, X, Video, Users as UsersIcon, Settings, ShieldCheck, Wallet, Monitor, MoreVertical, PhoneOff } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import { GET_DASHBOARD_STATS, GET_CREDITS, GET_ACTIVE_SESSIONS, GET_CURRENT_USER, GET_ORGANIZATION, GET_SUBSCRIPTION_PLAN } from "@/graphql/queries";
import { GENERATE_CONNECTION_TOKEN, END_SESSION } from "@/graphql/mutations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import StartSessionDialog from "@/components/dashboard/StartSessionDialog";
import StreamingSetupAssistant from "@/components/onboarding/StreamingSetupAssistant";
import OnboardingNudgeDialog from "@/components/onboarding/OnboardingNudgeDialog";
import { trackDashboardEvent } from "@/lib/analytics";
import { getAccessToken } from "@/lib/token-utils";

import { useState, useEffect, useMemo } from "react";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { ApiErrorScreen, useHasCriticalError } from "@/components/ApiErrorScreen";

type CurrentUserResponse = {
  me?: {
    name?: string;
    email?: string;
    createdAt?: string;
    onboardingCompleted?: boolean;
    streamingSetupCompleted?: boolean;
    ssoVerified?: boolean;
    organizations?: { id: string; name: string; role: string; isPersonal?: boolean }[];
  } | null;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [showOnboardingNudge, setShowOnboardingNudge] = useState(false);
  const [isStartSessionOpen, setIsStartSessionOpen] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const [showSSOBanner, setShowSSOBanner] = useState(false);
  const [rejoiningSessionId, setRejoiningSessionId] = useState<string | null>(null);
  const [pendingCloseSessionId, setPendingCloseSessionId] = useState<string | null>(null);
  const [closingSessionId, setClosingSessionId] = useState<string | null>(null);
  const [pendingJoinSession, setPendingJoinSession] = useState<{ id: string, roomName: string } | null>(null);

  const [generateConnectionToken] = useMutation(GENERATE_CONNECTION_TOKEN);
  const [endSession] = useMutation(END_SESSION, {
    refetchQueries: [{ query: GET_ACTIVE_SESSIONS }],
  });

  // Get organization ID and name from user's organizations
  const { data: userData, error: userError } = useQuery<CurrentUserResponse>(GET_CURRENT_USER, { errorPolicy: 'all' });
  const currentUser = useMemo(() => userData?.me || {}, [userData?.me]);
  // Extract first name only
  const getFirstName = (nameOrEmail: string) => {
    if (!nameOrEmail) return "User";
    if (nameOrEmail.includes('@')) {
      // If it's an email, use the part before @
      return nameOrEmail.split('@')[0].split('.')[0];
    }
    // Otherwise, use the first word of the name
    return nameOrEmail.split(' ')[0];
  };
  const userName = getFirstName(currentUser.name || currentUser.email || "User");
  const organizations = useMemo(() => userData?.me?.organizations || [], [userData?.me?.organizations]);
  // Use shared hook to get current organization consistently
  const { currentOrganizationId, currentOrg } = useCurrentOrganization(organizations);
  const organizationId = currentOrganizationId || null;

  // Get current month and year for dashboard stats
  const currentDate = new Date();
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const currentMonth = monthNames[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();

  // Fetch dashboard stats
  const { data: statsData, loading: statsLoading, error: statsError } = useQuery(GET_DASHBOARD_STATS, {
    variables: {
      month: currentMonth,
      year: currentYear,
    },
    errorPolicy: 'all',
  });

  // Fetch credits
  const { data: creditsData, error: creditsError } = useQuery<{ credits?: { balance?: number; overageLockActive?: boolean; usedThisMonth?: number } | null }>(GET_CREDITS, {
    errorPolicy: 'all',
  });

  // Fetch active sessions
  const { data: activeSessionsData, loading: activeSessionsLoading, error: activeSessionsError } = useQuery<{ 
    activeSessions?: { 
      id: string; 
      title?: string; 
      description?: string;
      flowRoomName?: string; 
      participants?: number;
      duration?: string;
      createdBy?: {
        name?: string;
        email?: string;
      };
    }[] 
  }>(GET_ACTIVE_SESSIONS, {
    errorPolicy: 'all',
  });

  const { data: subscriptionData, error: subscriptionError } = useQuery<{ subscription?: { plan?: string } | null }>(GET_SUBSCRIPTION_PLAN, {
    errorPolicy: 'all',
  });

  // Fetch organization data to check welcomeCreditsGranted
  const { data: orgData, error: orgError } = useQuery<{ organization?: { welcomeCreditsGranted?: boolean } | null }>(GET_ORGANIZATION, {
    variables: { id: organizationId },
    skip: !organizationId,
    errorPolicy: 'all',
  });

  // Check for any critical API errors
  const queryErrors = [userError, statsError, creditsError, activeSessionsError, orgError, subscriptionError];
  const hasCriticalErrors = useHasCriticalError(queryErrors);

  const credits = creditsData?.credits?.balance || 0;
  const overageLockActive = Boolean(creditsData?.credits?.overageLockActive || false);
  const usedThisMonth = creditsData?.credits?.usedThisMonth || 0;
  const stats = (statsData as { 
    dashboardStats?: {
      totalSessions: number;
      totalHours: number;
      avgCreditsPerSession: number;
      creditsUsed: number;
      recentSessions: {
        id: string;
        date: string;
        duration: string;
        creditsUsed: number;
      }[];
    }
  })?.dashboardStats;
  const activeSessions = activeSessionsData?.activeSessions || [];
  const welcomeCreditsGranted = orgData?.organization?.welcomeCreditsGranted ?? false;
  const currentPlan = subscriptionData?.subscription?.plan || "PERSONAL";
  const isPersonalPlan = currentPlan === "PERSONAL";
  // Check if we should show the welcome banner (only for owners)
  useEffect(() => {
    if (!currentUser?.createdAt) return;

    // Only show to owners
    const isOwner = currentOrg?.role?.toUpperCase() === 'OWNER';
    if (!isOwner) {
      setShowWelcomeBanner(false);
      return;
    }

    // Check if banner was dismissed
    const dismissed = localStorage.getItem('welcomeBannerDismissed');
    if (dismissed === 'true') {
      setShowWelcomeBanner(false);
      return;
    }

    // Calculate days since account creation
    const createdAt = new Date(currentUser.createdAt);
    const now = new Date();
    const daysSinceCreation = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));

    // Show banner if account is less than 7 days old
    if (daysSinceCreation <= 7) {
      setShowWelcomeBanner(true);
    }
  }, [currentUser, currentOrg]);

  // Check if we should show the SSO verification banner (only for owners)
  useEffect(() => {
    // Only show to owners
    const isOwner = currentOrg?.role?.toUpperCase() === 'OWNER';
    if (!isOwner) {
      setShowSSOBanner(false);
      return;
    }

    // Show banner if user is not SSO verified and organization hasn't received welcome credits
    const ssoVerified = currentUser?.ssoVerified;

    // Check if banner was dismissed
    const dismissed = localStorage.getItem('ssoBannerDismissed');
    if (dismissed === 'true') {
      setShowSSOBanner(false);
      return;
    }

    // Show banner if not SSO verified and no credits granted to organization
    if (ssoVerified === false && welcomeCreditsGranted === false) {
      setShowSSOBanner(true);
    } else {
      setShowSSOBanner(false);
    }
  }, [currentUser, currentOrg, welcomeCreditsGranted]);

  const handleDismissWelcomeBanner = () => {
    localStorage.setItem('welcomeBannerDismissed', 'true');
    setShowWelcomeBanner(false);
  };

  const handleDismissSSOBanner = () => {
    localStorage.setItem('ssoBannerDismissed', 'true');
    setShowSSOBanner(false);
  };

  // Check if we should show the streaming setup nudge
  const [showSetupAssistant, setShowSetupAssistant] = useState(false);
  const setupCompleted = currentUser?.streamingSetupCompleted ?? false;

  const handleJoinSession = async (sessionId: string, roomName: string) => {
    if (!roomName) return;

    setRejoiningSessionId(sessionId);
    trackDashboardEvent('session_action', {
      action: 'join_attempt',
      session_id: sessionId,
      room_name: roomName,
      source: 'dashboard_active_sessions',
    });
    try {
      const { data } = await generateConnectionToken({
        variables: {
          roomName: roomName,
          participantName: currentUser.name || currentUser.email || 'User',
        },
      });

      if ((data as { generateConnectionToken?: unknown })?.generateConnectionToken) {
        const authToken = getAccessToken();
        const getLivestreamUrl = () => {
          const baseUrl = typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
            ? `http://localhost:3001/${roomName}`
            : `https://meet.flowlabs.live/${roomName}`;

          if (authToken) {
            return `${baseUrl}?token=${encodeURIComponent(authToken)}`;
          }
          return baseUrl;
        };

        trackDashboardEvent('session_action', {
          action: 'join_success',
          session_id: sessionId,
          room_name: roomName,
          source: 'dashboard_active_sessions',
        });
        window.open(getLivestreamUrl(), '_blank');
      }
    } catch (error) {
      console.error('Failed to join session:', error);
      trackDashboardEvent('session_action', {
        action: 'join_failure',
        session_id: sessionId,
        room_name: roomName,
        source: 'dashboard_active_sessions',
        error_message: error instanceof Error ? error.message : 'join_session_failed',
      });
    } finally {
      setRejoiningSessionId(null);
    }
  };

  // Show blocking error screen when critical APIs fail
  if (hasCriticalErrors) {

    return (
      <ApiErrorScreen
        errors={queryErrors}
        context="Dashboard - Home"
      />
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 max-w-7xl">
      <div className="space-y-8">
        {/* Welcome Section */}
        <div className="space-y-2 flex items-center justify-between">
          <h1 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">
            Home
          </h1>
          <div className="flex items-center gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate('/settings?section=credits')}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-green-500/10 hover:bg-green-500/20 text-green-500 transition-colors"
                >
                  <Wallet className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{usedThisMonth.toLocaleString()} credits used this month</p>
              </TooltipContent>
            </Tooltip>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings?section=room')}
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
              Room Settings
            </Button>
          </div>
        </div>

        {/* SSO Verification Banner - For email-only users */}
        {showSSOBanner && (
          <Alert className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800 relative animate-fade-in">
            <ShieldCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <button
              onClick={handleDismissSSOBanner}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            >
              <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="sr-only">Close</span>
            </button>
            <AlertTitle className="text-blue-800 dark:text-blue-200 font-semibold text-lg flex items-center gap-2">
              <Gift className="h-4 w-4" />
              Get 100 Free Credits!
            </AlertTitle>
            <AlertDescription className="text-blue-700 dark:text-blue-300">
              <p className="mt-1 mb-3">
                Verify your identity by connecting your Google account to receive{" "}
                <span className="font-bold text-blue-900 dark:text-blue-100">100 free streaming credits</span>.
              </p>
              <Link to="/settings?section=profile&tab=verification">
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Connect Google Account
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {/* Welcome Bonus Banner */}
        {showWelcomeBanner && (
          <Alert className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800 relative animate-fade-in">
            <Gift className="h-5 w-5 text-green-600 dark:text-green-400" />
            <button
              onClick={handleDismissWelcomeBanner}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            >
              <X className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="sr-only">Close</span>
            </button>
            <AlertTitle className="text-green-800 dark:text-green-200 font-semibold text-lg">
              Welcome to FlowLabs!
            </AlertTitle>
            <AlertDescription className="text-green-700 dark:text-green-300">
              <p className="mt-1">
                To get you started, we've added <span className="font-bold text-green-900 dark:text-green-100">100 free credits</span> to your account!
                Start streaming and explore all the features FlowLabs has to offer.
              </p>
            </AlertDescription>
          </Alert>
        )}



        {/* Hero CTA */}
        <Card className="p-12 border-0 text-center animate-gradient-slow">
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div className="space-y-3">
              <h2 className="text-3xl font-bold text-white">Ready to stream?</h2>
              <p className="text-white/80 text-lg">
                Start a high-fidelity streaming session with your team
              </p>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Button
                size="lg"
                data-tour-id="dashboard-start-session"
                onClick={() => {
                  if (!setupCompleted) {
                    setShowOnboardingNudge(true);
                  } else {
                    setIsStartSessionOpen(true);
                  }
                }}
                className="bg-white text-background hover:bg-white/90 font-semibold px-8 h-14 text-lg"
              >
                <Plus className="mr-2 w-6 h-6" />
                Start New Session
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate('/calculator')}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all text-white/80 hover:text-white"
                  >
                    <Calculator className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Estimate session cost</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </Card>

        {/* Streaming Setup Nudge */}
        {!setupCompleted && (
          <Card className="p-6 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20 animate-fade-in shadow-sm hover:shadow-md transition-shadow">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
              <div className="flex flex-col md:flex-row items-center gap-4 text-left">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Monitor className="w-6 h-6 text-primary" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-xl font-bold">Configure your streaming setup</h3>
                  <p className="text-muted-foreground text-sm">
                    Get ready for your first session by choosing your video path and testing your connection.
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => setShowSetupAssistant(true)}
                className="bg-primary text-white hover:opacity-90 px-8 whitespace-nowrap shadow-lg shadow-primary/20"
              >
                Complete Setup
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Separator */}
        <div className="border-t border-border" />

        {/* Quick Stats */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Monthly Metrics</h2>
            <div className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
              {currentMonth} {currentYear}
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <Card className="p-6 bg-gradient-to-br from-card to-card/50 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg group animate-fade-in">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-muted-foreground">Total Sessions</p>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                </div>
                {statsLoading ? (
                  <Skeleton className="h-10 w-20" />
                ) : (
                  <p className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">
                    {stats?.totalSessions ?? 0}
                  </p>
                )}
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-card to-card/50 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg group animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-muted-foreground">Total Hours</p>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Plus className="w-5 h-5 text-primary" />
                  </div>
                </div>
                {statsLoading ? (
                  <>
                    <Skeleton className="h-10 w-16" />
                    <p className="text-xs text-muted-foreground">Streaming time</p>
                  </>
                ) : (
                  <>
                    <p className="text-4xl font-bold">
                      {stats?.totalHours?.toFixed(1) ?? "0.0"}
                    </p>
                    <p className="text-xs text-muted-foreground">Streaming time</p>
                  </>
                )}
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-card to-card/50 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg group animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-muted-foreground">Avg Credits Per Room</p>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ArrowRight className="w-5 h-5 text-primary" />
                  </div>
                </div>
                {statsLoading ? (
                  <>
                    <Skeleton className="h-10 w-14" />
                    <p className="text-xs text-muted-foreground">Per session</p>
                  </>
                ) : (
                  <>
                    <p className="text-4xl font-bold">
                      {stats?.avgCreditsPerSession?.toFixed(0) ?? "0"}
                    </p>
                    <p className="text-xs text-muted-foreground">Per session</p>
                  </>
                )}
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-card to-card/50 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg group animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-muted-foreground">Credits Used</p>
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Plus className="w-5 h-5 text-green-500" />
                  </div>
                </div>
                {statsLoading ? (
                  <>
                    <Skeleton className="h-10 w-20" />
                    <p className="text-xs text-muted-foreground">This month</p>
                  </>
                ) : (
                  <>
                    <p className="text-4xl font-bold text-green-500">
                      {stats?.creditsUsed?.toLocaleString() ?? "0"}
                    </p>
                    <p className="text-xs text-muted-foreground">This month</p>
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-border" />

        {/* Active Sessions */}
        <div className="space-y-4" data-tour-id="dashboard-active-sessions">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Active Sessions</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/history')}
              className="gap-2"
            >
              View History
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          {activeSessionsLoading ? (
            <Card className="p-6 bg-card border-border">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
                <Skeleton className="h-10 w-28" />
              </div>
            </Card>
          ) : activeSessions.length > 0 ? (
            <div className="grid gap-4">
              {activeSessions.map((session) => (
                <Card
                  key={session.id}
                  className="p-6 bg-card border-border hover:border-primary/50 transition-all group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        {session.title ? (
                          <h3 className="font-semibold text-lg">{session.title}</h3>
                        ) : (
                          <h3 className="font-semibold text-lg">Session {session.id}</h3>
                        )}
                        <Badge variant="secondary" className="text-xs">
                          <Video className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <UsersIcon className="w-4 h-4" />
                          <span>{session.participants} participant{session.participants !== 1 ? 's' : ''}</span>
                        </div>
                        {session.createdBy && (
                          <div className="flex items-center gap-2">
                            <span>•</span>
                            <span>Created by {session.createdBy.name || session.createdBy.email}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span>•</span>
                          <span>{session.duration}</span>
                        </div>
                      </div>

                      {session.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{session.description}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!session.flowRoomName) return;

                          if (!setupCompleted) {
                            setPendingJoinSession({ id: session.id, roomName: session.flowRoomName });
                            setShowOnboardingNudge(true);
                            return;
                          }

                          setRejoiningSessionId(session.id);
                          trackDashboardEvent('session_action', {
                            action: 'join_attempt',
                            session_id: session.id,
                            room_name: session.flowRoomName,
                            source: 'dashboard_active_sessions',
                          });
                          try {
                            const { data } = await generateConnectionToken({
                              variables: {
                                roomName: session.flowRoomName,
                                participantName: currentUser.name || currentUser.email || 'User',
                              },
                            });

                            if ((data as { generateConnectionToken?: unknown })?.generateConnectionToken) {
                              const authToken = getAccessToken();
                              const getLivestreamUrl = () => {
                                const baseUrl = typeof window !== 'undefined' &&
                                  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
                                  ? `http://localhost:3001/${session.flowRoomName}`
                                  : `https://meet.flowlabs.live/${session.flowRoomName}`;

                                if (authToken) {
                                  return `${baseUrl}?token=${encodeURIComponent(authToken)}`;
                                }
                                return baseUrl;
                              };

                              trackDashboardEvent('session_action', {
                                action: 'join_success',
                                session_id: session.id,
                                room_name: session.flowRoomName,
                                source: 'dashboard_active_sessions',
                              });
                              window.open(getLivestreamUrl(), '_blank');
                            }
                          } catch (error) {
                            console.error('Failed to join session:', error);
                            trackDashboardEvent('session_action', {
                              action: 'join_failure',
                              session_id: session.id,
                              room_name: session.flowRoomName,
                              source: 'dashboard_active_sessions',
                              error_message: error instanceof Error ? error.message : 'join_session_failed',
                            });
                          } finally {
                            setRejoiningSessionId(null);
                          }
                        }}
                        disabled={rejoiningSessionId === session.id || closingSessionId === session.id}
                        className="gap-2"
                      >
                        {rejoiningSessionId === session.id ? 'Joining...' : (
                          <>
                            <Video className="w-4 h-4" />
                            Join Session
                          </>
                        )}
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                            disabled={closingSessionId === session.id || rejoiningSessionId === session.id}
                            className="h-8 w-8 p-0"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingCloseSessionId(session.id);
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <PhoneOff className="w-4 h-4 mr-2" />
                            {closingSessionId === session.id ? 'Closing...' : 'Force Close'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-6 bg-card border-border">
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">
                  No active sessions. Start one to collaborate with your team!
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Overlay Dialogs */}
      <OnboardingNudgeDialog 
        open={showOnboardingNudge} 
        onOpenChange={setShowOnboardingNudge}
        onCompleteSetup={() => {
          setShowOnboardingNudge(false);
          setShowSetupAssistant(true);
        }}
        onSkip={() => {
          setShowOnboardingNudge(false);
          if (pendingJoinSession) {
            handleJoinSession(pendingJoinSession.id, pendingJoinSession.roomName);
            setPendingJoinSession(null);
          } else {
            setIsStartSessionOpen(true);
          }
        }}
      />

      <StreamingSetupAssistant
        open={showSetupAssistant}
        onOpenChange={(open) => {
          setShowSetupAssistant(open);
          if (!open) setPendingJoinSession(null);
        }}
      />

      <StartSessionDialog
        open={isStartSessionOpen}
        onOpenChange={setIsStartSessionOpen}
        credits={credits}
        isPersonalPlan={isPersonalPlan}
        overageLockActive={overageLockActive}
      />

      {/* Force Close Confirmation */}
      <AlertDialog
        open={Boolean(pendingCloseSessionId)}
        onOpenChange={(open) => { if (!open) setPendingCloseSessionId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force close session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately end the session for all participants and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                const sessionId = pendingCloseSessionId;
                setPendingCloseSessionId(null);
                if (!sessionId) return;
                setClosingSessionId(sessionId);
                try {
                  await endSession({ variables: { sessionId } });
                } catch (error) {
                  console.error('Failed to force close session:', error);
                } finally {
                  setClosingSessionId(null);
                }
              }}
            >
              Force Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
